import {testIdProps} from '@github-ui/test-id-props'
import {AlertIcon} from '@primer/octicons-react'
import {Banner, Button, Flash, Heading, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useRef, useState} from 'react'

import {RenameProjectFieldDialog} from '../../../components/rename-project-field-dialog'
import type {ColumnWarningKind} from '../../../helpers/get-column-warning'
import type {ColumnModel} from '../../../models/column-model'
import {ColumnBannerResources, RenameProjectFieldDialogResources} from '../../../strings'
import styles from './column-settings-banner.module.css'

type ColumnSettingsBannerProps = {
  column: ColumnModel
  warning: ColumnWarningKind
}

export function ColumnSettingsBanner({column, warning}: ColumnSettingsBannerProps) {
  if (warning === 'rename-custom-type-column') {
    return <RenameBanner column={column} />
  }
  if (warning === 'column-name-collides-with-issue-field') {
    return <NamingConflictBanner column={column} />
  }
  return null
}

function RenameBanner({column}: {column: ColumnModel}) {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  return (
    <>
      <Flash {...testIdProps('column-settings-banner')} variant="warning" aria-live="polite" className={styles.Flash}>
        <div className={styles.Box}>
          <div>
            <Octicon icon={AlertIcon} className={styles.Octicon} />
          </div>
          <div className={styles.Box_1}>
            <Heading as="h3" className={styles.Heading}>
              {ColumnBannerResources.IssueTypeRename.copy1}
            </Heading>
            {ColumnBannerResources.IssueTypeRename.copy2(column.name)}&nbsp;
            <Link target="_blank" rel="noopener noreferrer" href={ColumnBannerResources.IssueTypeRename.learnMoreLink}>
              {ColumnBannerResources.IssueTypeRename.learnMoreText}
            </Link>
          </div>
          <div className={styles.Box_2}>
            <Button ref={buttonRef} onClick={() => setIsRenameDialogOpen(true)}>
              {ColumnBannerResources.IssueTypeRename.actionPrimary}
            </Button>
          </div>
        </div>
      </Flash>
      {isRenameDialogOpen && (
        <RenameProjectFieldDialog
          column={column}
          onCancel={() => {
            setIsRenameDialogOpen(false)
          }}
          onConfirm={() => {
            setIsRenameDialogOpen(false)
          }}
          variant="issue-type"
        />
      )}
    </>
  )
}

function NamingConflictBanner({column}: {column: ColumnModel}) {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)

  return (
    <>
      <Banner
        {...testIdProps('column-settings-banner')}
        className={styles.BannerMarginBottom}
        aria-label={RenameProjectFieldDialogResources.IssueFieldVariant.title}
        title={RenameProjectFieldDialogResources.IssueFieldVariant.title}
        description={RenameProjectFieldDialogResources.IssueFieldVariant.description(column.name)}
        variant="warning"
        primaryAction={<Banner.PrimaryAction onClick={() => setIsRenameDialogOpen(true)}>Resolve</Banner.PrimaryAction>}
      />
      {isRenameDialogOpen && (
        <RenameProjectFieldDialog
          column={column}
          onCancel={() => {
            setIsRenameDialogOpen(false)
          }}
          onConfirm={() => {
            setIsRenameDialogOpen(false)
          }}
          variant="issue-field"
        />
      )}
    </>
  )
}
